<script>
import { reactive, ref, toRefs, computed } from 'vue'
import { useStore } from 'vuex'
import { addCompRegMaterial, getCompRegInfo, updateCompRegInfo } from '@/api/modules/auth'
import { joinFile, routeTo } from '@/utils'
import SrUpload from '@/components/SrUpload/index.vue'
import ResetAuthTypeDialog from '@/components/ResetAuthTypeDialog'
import CertTypeSelect from "./components/cert-type-select"
import fileComp from "@/compositions/modules/file"
import { vab } from "@libs/utils"
import { useI18n } from "@/plugins/i18n"
import countryCode from '@/api/modules/countryCode'

export default {
  name: 'AuthRegister',
  components: { SrUpload, ResetAuthTypeDialog, CertTypeSelect },
  setup() {
    const store = useStore()
    const { FILE } = store.state.base.constant
    const nextOrUpdate = computed(() => +store.state.auth.step !== 4 ? 'next' : 'update')
    const { template: { idCardFront, idCardBack, authPdf } } = fileComp()
    const refs = reactive({ formRef: null, authFile: authPdf })
    const { t } = useI18n()

    const { formReq, rules, loadingScope } = initForm()
    const certRef = ref(null)
    const areaCountryList = store.state.base.dict.country.countryCodeList

    // 处理下一步
    const handleNext = async () => {
      const userId = store.state.user.userId
      const stepComplete = store.state.auth.registerInfo?.stepComplete || null
      const regType = store.state.auth.registerInfo?.regType || null

      refs.formRef.validate(async valid => {
        if (valid) {
          // 如果是授权代表人，需要验证cert-type-select组件表单
          if (formReq.userType === 'A') {
            const certValid = await certRef.value?.validateForm()
            if (!certValid) return
          }
          
          loadingScope.btnLoading = true
          
          // 从cert-type-select组件获取文件ID（如果是授权代表人）
          let certType = ''
          let backFileId = ''
          let frontFileId = ''
          let holdFileId = ''
          
          if (formReq.userType === 'A' && certRef.value) {
            const certForm = certRef.value.form
            certType = certForm.certType
            backFileId = certForm.backFileId
            frontFileId = certForm.frontFileId
            holdFileId = certForm.holdFileId
          } else {
            // 法定代表人使用表单中的值
            certType = 'I' // 默认身份证类型
            backFileId = formReq.backFileId
            frontFileId = formReq.frontFileId
            holdFileId = formReq.holdFileId
          }
          
          const { userType, authFileId, regName, countryCode, regId = '' } = formReq
          const param = { 
            userId, 
            userType, 
            authFileId, 
            certType,
            idType:certType,
            backFileId, 
            frontFileId, 
            holdFileId,
            regName,
            countryCode 
          }
          
          try {
            const res = regId ? (await updateCompRegInfo({ ...param, regId })) : (await addCompRegMaterial(param))
            loadingScope.btnLoading = false
            await store.dispatch('auth/setRegId', regId || res.regId)
            routeTo(((+stepComplete === 1 || nextOrUpdate.value === 'update') && (+regType !== 1)) ? '/auth/company/overview' : '/auth/company/info')
          } catch (err) {
            loadingScope.btnLoading = false
            console.log(err)
          }
        }
      })
    }

    return {
      ...toRefs(refs),
      ...toRefs(loadingScope),
      FILE,
      areaCountryList,
      formReq,
      rules,
      idCardFront,
      idCardBack,
      ...toRefs(initDialog()),
      t,
      handleNext,
      certRef
    }
  }
}

const initForm = () => {
  const store = useStore()
  const { t } = useI18n()
  const formReq = reactive({
    authFileId: "", // 授权文件id
    authFileName: "", // 授权文件name
    backFileId: "", // 证件反面文件
    backFileName: "", // 证件反面文件名字
    frontFileId: "", // 证件正面文件
    frontFileName: "", // 证件正面文件名字
    userId: "", // 会员用户ID
    regName: "", // 个人:真实姓名 企业:法人姓名，授权人姓名
    userType: "L", // 身份类型:L-法定代表人,A-授权代表人
    countryCode: "", // 证件签发地
    idType: "I", // 证件类型:I-身份证
  })

  // validate function
  const validateRegisterName = (rule, value, callback) => {
    if (value === '') {
      if (formReq.userType === 'L') {
        callback(new Error(t('auth.company.register.error.text_2')))
      } else {
        callback(new Error(t('auth.company.register.placeholder.text_2')))
      }
    } else {
      callback()
    }
  }

  const rules = reactive({
    userType: [
      { required: true, message: t('auth.company.register.error.text_4'), trigger: 'change' }
    ],
    countryCode: [
      { required: true, message: t('auth.company.register.error.issuingCountry'), trigger: 'change' }
    ],
    regName: [
      { required: true, validator: validateRegisterName, trigger: 'blur' }
    ]
  })

  // loading
  const loadingScope = reactive({
    pageLoading: false,
    btnLoading: false
  })

  // 初始化
  const initDate = async () => {
    loadingScope.pageLoading = true
    try {
      const data = await getCompRegInfo()
      store.dispatch('auth/setStep', data.regName ? false : '0')
      if (data.regName) {
        formReq.authFileId = data.authFileId
        formReq.backFileId = data.backFileId
        formReq.frontFileId = data.frontFileId
        formReq.regName = data.regName
        formReq.userId = data.userId
        formReq.userType = data.userType
        formReq.countryCode = data.countryCode
        formReq.regId = data.regId
        formReq.regCheckStatus = data.regCheckStatus
        formReq.idType = data.idType
      }
      loadingScope.pageLoading = false
    } catch (err) {
      loadingScope.pageLoading = false
      console.log(err)
    }
  }

  initDate()

  return { formReq, rules, loadingScope }
}

// 处理弹框
const initDialog = () => {
  const dialogVisible = ref(false)
  return { dialogVisible }
}
</script>

<template>
  <div>
    <div class="company-auth-register-container">
      <el-form
          :model="formReq"
          :rules="rules"
          ref="formRef"
          :disabled="formReq.regCheckStatus && ['CF', 'CP'].includes(formReq.regCheckStatus)"
          :label-width="localCN?'150px':'230px'"
          label-position="left"
          v-loading="pageLoading">
        <el-row>
          <el-col :span="24">
            <div class="h1">{{t('auth.company.register.title.text_1')}}</div>

            <!--身份类型-->
            <el-form-item
                prop="userType"
                :label="t('auth.company.register.label.text_1')">
              <el-radio-group v-model="formReq.userType">
                <el-radio-button label="L">{{t('auth.company.register.label.text_2')}}</el-radio-button>
                <el-radio-button label="A">{{t('auth.company.register.label.text_3')}}</el-radio-button>
              </el-radio-group>
            </el-form-item>


            <el-form-item prop="regName" :label="formReq.userType === 'L' ? t('auth.company.register.label.text_4') : t('auth.company.register.label.text_5')">
              <el-input :placeholder="formReq.userType === 'L' ? t('auth.company.register.placeholder.text_1') : t('auth.company.register.placeholder.text_2')" v-model="formReq.regName"/>
            </el-form-item>

            
            <!--授权书-->
            <div v-if="formReq.userType === 'A'">
              <el-form-item :label="t('auth.company.register.label.text_6')" prop="authFileId"
                                    :rules="{required: true, message: t('auth.company.register.error.text_1'), trigger:'change'}">
                <div class="auth-upload-container">
                  <div class="upload-title-wrap">
                    <div class="h2" style="height: 40px;line-height: 40px;" >{{t('auth.company.register.content.text_5')}}
                      <a class="a" download="授权书模板.pdf" :href="authFile">{{t('auth.company.register.content.text_5_2')}}</a>
                    </div>
                  </div>
                  <div class="auth-upload-wrap">

                    <!--授权书-->
                    <div class="auth-upload-item">
                      <el-form-item class="upload-item" >
                        <sr-upload
                            v-model:fileId="formReq.authFileId"
                            v-model:fileName="formReq.authFileName"
                            :filePrefix="FILE.PREFIX.AUTH_FILE"
                            fileSize="5"
                            :uploadDesc="t('auth.company.register.label.text_6')"
                            :mode="FILE.UPLOAD_MODE.EDITABLE"/>
                      </el-form-item>
                      <div class="h3">{{t('auth.company.register.label.text_6')}}</div>
                    </div>

                  </div>
                </div>
              </el-form-item>
              <el-form-item label="" >
                <div class="auth-upload-container">
                  <div class="h3">
                    {{t('auth.company.register.content.text_6')}}
                  </div>

                  <div class="h3">
                    {{t('auth.company.register.content.text_7')}}
                  </div>
                </div>
              </el-form-item>
            </div>
            <!--证件签发地-->
            <el-form-item prop="countryCode"
             v-if="formReq.userType === 'A'"
              :label="t('auth.company.register.label.issuingCountry')">
              <el-select
                  v-model="formReq.countryCode"
                  :placeholder="t('auth.company.register.error.issuingCountry2')"
                  filterable>
                <el-option
                    v-for="country in areaCountryList"
                    :key="country.value"
                    :label="country.label"
                    :value="country.value">
                  </el-option>
              </el-select>

            </el-form-item>
            <!--证件类型选择-->
            <div v-if="formReq.userType === 'A'">
                <cert-type-select 
                  ref="certRef"
                  :scopeData="{
                    holdInterval: false,
                    certType: formReq.idType,
                    userType: 'A',
                    frontFileId: formReq.frontFileId,
                    compUserId: null,
                    backFileId: formReq.backFileId,
                  }"
                />
            </div>
          </el-col>
        </el-row>

        <el-row>
          <el-col :span="24">
            <el-form-item :class="{'el-form__next': formReq.userType === 'L'}">
              <el-button
                  class="el-button__next"
                  type="primary"
                  v-loading="btnLoading"
                  :disabled="btnLoading"
                  @click="handleNext">{{t('auth.personal.register.content.text_6')}}
              </el-button>
              <div class="a" style="margin-left: 50px;" @click="dialogVisible = true">{{t('auth.personal.register.content.text_7')}}</div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <reset-auth-type-dialog :viewVisible="dialogVisible" @close="dialogVisible = false"/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .company-auth-register-container {
    padding: 20px;
    background-color: #fff;

    .auth-upload-container {
      width: 100%;
      box-sizing: border-box;

      .auth-upload-wrap {
        width: 100%;
        display: flex;

        .auth-upload-item {
          flex: 1;
          margin-right: 60px;

          .upload-item {
            margin-bottom: 15px;
          }
        }
      }
    }
  }

  :deep(.el-form-item__content) {
    flex: inherit !important;
    width: auto !important;

    .el-input__inner, .el-textarea__inner {
      width: 360px !important;
      border-radius: 4px !important;
    }
    .el-radio-button__inner{
      width: auto;
      padding: 12px 19px;
    }
    .el-select{
      width: 390px !important;
      border-radius: 4px !important;
    }
    .el-input__wrapper{
      width: 100%;
    }
  }

  .el-form__next {
    padding-top: 300px;
  }
  
</style>
